import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import type { Summary } from '../../hooks/useAnalytics'

function formatDuration(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return '—'
  if (ms < 1000) return `${Math.round(ms)} ms`
  return `${(ms / 1000).toFixed(2)} s`
}

function formatCount(n: number): string {
  return n.toLocaleString()
}

export interface Tile {
  label: string
  value: string
  sub?: string
  tone?: 'default' | 'warn'
}

/**
 * Depth decides which numbers are honest to lead with.
 *
 * `business` answers "what are our clients doing, and how is it going for
 * them". `technical` adds the request-level truth underneath it. The split
 * exists because most people reading this dashboard are not engineers, and a
 * tile reading "431,653 events" invites exactly the wrong conclusion — 89% of
 * that is our own polling.
 */
export type Depth = 'business' | 'technical'

export function tilesFromSummary(summary: Summary, depth: Depth = 'business'): Tile[] {
  const activity: Tile = {
    // The hero number, and deliberately NOT "events". A user showing 571
    // events had clicked nowhere near 571 times. This counts one interaction
    // once: screens opened and things done, with repeats of the same thing
    // inside a minute collapsed.
    label: 'Activity',
    value: formatCount(summary.activity),
    sub: `${formatCount(summary.views)} screens · ${formatCount(summary.actions)} actions`,
  }

  const people: Tile = {
    label: 'People',
    value: formatCount(summary.uniqueUsers),
    sub: `${formatCount(summary.sessions)} visits · ${formatCount(summary.uniqueVisitors)} devices`,
  }

  if (depth === 'business') {
    return [
      activity,
      people,
      {
        label: 'Per visit',
        value: summary.sessions > 0 ? (summary.activity / summary.sessions).toFixed(1) : '—',
        sub: 'things done in an average visit',
      },
      {
        // "Experiencing", in plain words. Null rather than 0% when nothing in
        // the window carried a recorded status — all backfilled history.
        label: 'Problems',
        value: summary.apiErrorRate === null ? '—' : formatCount(summary.apiErrors),
        sub: summary.apiErrorRate === null
          ? 'not measured for this range'
          : `${(summary.apiErrorRate * 100).toFixed(1)}% of requests failed`,
        tone: summary.apiErrorRate !== null && summary.apiErrorRate > 0.05 ? 'warn' : 'default',
      },
    ]
  }

  return [
    // NOT "every event": the summary is scoped to activity kinds, so assets and
    // platform plumbing — about 95% of stored rows — are already excluded.
    { label: 'Events', value: formatCount(summary.events), sub: 'requests, before collapsing' },
    activity,
    {
      label: 'Repeats collapsed',
      value: formatCount(summary.repeats),
      sub: `${formatCount(summary.rawInteractions)} before collapsing`,
    },
    {
      // Names the fan-out for what it is instead of presenting it as usage.
      label: 'Background',
      value: formatCount(summary.background),
      sub: summary.fanoutRatio === null
        ? 'polling, bootstrap, config'
        : `${summary.fanoutRatio.toFixed(1)} requests per interaction`,
      tone: summary.fanoutRatio !== null && summary.fanoutRatio > 20 ? 'warn' : 'default',
    },
    {
      label: 'API calls',
      value: formatCount(summary.apiCalls),
      sub: summary.apiErrorRate === null
        ? 'error rate not measured'
        : `${(summary.apiErrorRate * 100).toFixed(1)}% errors`,
      tone: summary.apiErrorRate !== null && summary.apiErrorRate > 0.05 ? 'warn' : 'default',
    },
    {
      label: 'Avg response',
      value: formatDuration(summary.avgDurationMs),
      sub: summary.avgDurationMs === null ? 'not measured' : 'downstream worker',
    },
  ]
}

export function StatTiles({ summary, loading, depth = 'business' }: {
  summary: Summary | null
  loading: boolean
  depth?: Depth
}) {
  const columns = depth === 'business' ? 'lg:grid-cols-4' : 'lg:grid-cols-6'

  if (loading && !summary) {
    return (
      <div className={`grid gap-4 grid-cols-2 ${columns} mb-6`}>
        {Array.from({ length: depth === 'business' ? 4 : 6 }).map((_, i) => (
          <Card key={i}><CardContent className="p-5"><Skeleton className="h-14 w-full" /></CardContent></Card>
        ))}
      </div>
    )
  }
  if (!summary) return null

  return (
    <div className={`grid gap-4 grid-cols-2 ${columns} mb-6`}>
      {tilesFromSummary(summary, depth).map(tile => (
        <Card key={tile.label}>
          <CardContent className="p-5">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {tile.label}
            </div>
            <div className={`mt-2 text-2xl font-semibold tabular-nums ${tile.tone === 'warn' ? 'text-amber-600 dark:text-amber-500' : 'text-foreground'}`}>
              {tile.value}
            </div>
            {tile.sub && <div className="mt-1 text-xs text-muted-foreground">{tile.sub}</div>}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
